import React, { useCallback } from 'react';
import classNames from 'classnames';
import { formatSecondsToMinutesSeconds } from '@common/formatSecondsToMinutesSeconds';
import AppGeneralPopup from '../../../../common/app_general_popup';
import classes from '../classes.module.scss';

interface ResendOptionsModalProps {
  visible: boolean;
  onClose: () => void;
  onResendCode: () => void;
  onEditNumber: () => void;
  smsCooldown?: number;
  loading?: boolean;
  t: (key: string) => string;
}

/**
 * ResendOptionsModal component - Modal with resend options
 * Displays options to resend code or edit phone number
 */
function ResendOptionsModal({
  visible,
  onClose,
  onResendCode,
  onEditNumber,
  smsCooldown = 0,
  loading = false,
  t
}: ResendOptionsModalProps) {
  const handleResendCode = useCallback(() => {
    if (smsCooldown > 0 || loading) {
      return;
    }
    onResendCode();
    onClose();
  }, [smsCooldown, loading, onResendCode, onClose]);

  const handleEditNumber = useCallback(() => {
    if (loading) {
      return;
    }
    onEditNumber();
    onClose();
  }, [loading, onEditNumber, onClose]);

  const isResendDisabled = smsCooldown > 0 || loading;

  return (
    <AppGeneralPopup
      visible={ visible }
      closable={ onClose }
      closeButtonAction={ null }
      backArrowAction={ null }
      backCustomAction={ null }
      rightCustomItem={ null }
      title={ null }
      customTopHeader={ null }
      logoImg={ null }
      withLogo={ false }
    >
      <div className={ classes.resendOptionsModalContainer }>
        {/* Title */}
        <div className={ classes.resendOptionsModalTitle }>
          <p className={ classes.resendOptionsModalTitleText }>
            {t('smsVerification.need_help')}
          </p>
        </div>

        {/* Options */}
        <div className={ classes.resendOptionsModalOptions }>
          <button
            type="button"
            onClick={ handleResendCode }
            className={ classNames(
              classes.resendOptionsModalOption,
              isResendDisabled ? classes.resendOptionsModalOptionDisabled : null
            ) }
            disabled={ isResendDisabled }
          >
            {smsCooldown > 0
              ? `${ t('smsVerification.resend_code') } (${ formatSecondsToMinutesSeconds(smsCooldown) })`
              : t('smsVerification.resend_code')}
          </button>

          <button
            type="button"
            onClick={ handleEditNumber }
            className={ classNames(
              classes.resendOptionsModalOption,
              loading ? classes.resendOptionsModalOptionDisabled : null
            ) }
            disabled={ loading }
          >
            {t('smsVerification.edit_number')}
          </button>
        </div>

        {/* Cancel Button */}
        <button
          type="button"
          onClick={ onClose }
          className={ classes.resendOptionsModalCancelButton }
          disabled={ loading }
        >
          {t('general.cancel')}
        </button>
      </div>
    </AppGeneralPopup>
  );
}

export default ResendOptionsModal;
